import { get, union } from 'lodash'

import { getUUIDv4 } from '@/src/utils/helper'
import { convertLinkToBackEnd } from '@/src/utils/url'
import { createMapboxImageUrl } from '@components/Map/helper'

import { LAYER_TYPE } from '../layer'
import { ALayer } from './ALayer'

const SOURCE = {}
function createSource(url, select = []) {
  return `${url}?s=${select.join(',')}`
}
export class NbdsVectorLayer extends ALayer {
  get layer_ids() {
    return this.layers.map((x) => x.id)
  }
  constructor(options) {
    let info = options.info
    super({
      metadata: {
        bounds: info.bounds,
        fields: get(info, 'datastore_detail.fields', []),
      },
      type: 'circle',
    })
    this.url = convertLinkToBackEnd(info.tile_link)
    this.layerId = `layer-${getUUIDv4()}`
    this.sourceId = `source-${info.tile_link}`
    let { layers, fields, image_ids } = convertNbdsStyleToMapBoxStyle(
      info.style
    )
    this.image_ids = image_ids
    this.image_urls = this.image_ids.map((id) =>
      convertLinkToBackEnd(`api/image/${id}.png`)
    )
    if (!SOURCE[this.url]) {
      SOURCE[this.url] = { fields: [] }
    }
    SOURCE[this.url].fields = union(SOURCE[this.url].fields, fields)
    this.source = {
      tiles: [createSource(this.url, SOURCE[this.url].fields)],
      type: 'vector',
      bounds: info.bounds || [-180, -85.051129, 180, 85.051129],
    }
    this.info = info
    this.layers = layers
  }
  convertDefaultView(layer_view) {}
  getStyle() {
    return this.layers[0]
  }
  getBeforeId() {
    return this.layers[this.layers.length - 1].id
  }
  async addToMap(map) {
    if (!map.getSource(this.sourceId)) {
      map.addSource(this.sourceId, this.source)
    }
    if (this.image_ids && this.image_ids.length > 0) {
      await Promise.all(
        this.image_ids.map((id) =>
          createMapboxImageUrl(
            map.id,
            '_custom_image_' + id,
            convertLinkToBackEnd(`api/image/${id}.png`)
          )
        )
      )
    }
    this.layers.forEach((layer, i) => {
      let layer_id = layer.id || `${this.layerId}-${i}`
      layer.id = layer_id
      if (!map.getLayer(layer_id)) {
        let temp = {
          ...layer,
          id: layer_id,
          'source-layer': 'tile-server',
          source: this.sourceId,
          metadata: {
            layer_type: LAYER_TYPE.NBDS_VECTO_LAYER,
            layer_id: this.info.id,
          },
        }
        layer = temp
        map.addLayer(temp)
      }
    })
  }
  removeFromMap(map) {
    this.layers.forEach((layer, i) => {
      let layer_id = layer.id || `${this.layerId}-${i}`

      if (map.getLayer(layer_id)) {
        map.removeLayer(layer_id)
      }
    })
    if (this.layer_label && map.getLayer(this.layer_label.id)) {
      map.removeLayer(this.layer_label.id)
    }
    const layers = this.layer_labels ?? []
    layers.forEach((layer) => {
      if (map.getLayer(layer.id)) {
        map.removeLayer(layer.id)
      }
    })
    if (map.getSource(this.sourceId)) {
      map.removeSource(this.sourceId)
    }
  }
  moveLayer(map, beforeId) {
    let temp_before_id = beforeId
    const layers = [...this.layers, ...(this.layer_labels ?? [])]
    if (this.layer_label && this.layer_label.id) {
      layers.push(this.layer_label)
    }
    layers.forEach((layer, i) => {
      let layer_id = layer.id || `${this.layerId}-${i}`
      map.moveLayer(layer_id, temp_before_id)
      temp_before_id = layer_id
    })
  }
  toggleShow(map, show) {
    this.layers.forEach((layer, i) => {
      let layer_id = layer.id || `${this.layerId}-${i}`
      map.setLayoutProperty(layer_id, 'visibility', show ? 'visible' : 'none')
    })
  }
  setOpacity(map, opacity) {
    this.layers.forEach((layer, i) => {
      let layer_id = layer.id || `${this.layerId}-${i}`
      let keyOpacity = `${layer.type}-opacity`
      map.setPaintProperty(layer_id, keyOpacity, opacity)
    })
  }

  setLabelStyle(map, style) {
    if (this.layer_labels)
      this.layer_labels.forEach((layer, i) => {
        let layer_id = layer.id || `${this.layerId}-${i}`
        if (map.getLayer(layer_id)) {
          map.removeLayer(layer_id)
        }
      })
    this.layer_labels = undefined
    if (!this.info.style) {
      this.info.style = {}
    }
    this.info.style.content_label = style.content_label || []
    this.showLabel(map, !!this.label_show)
  }
  setStyle(map, style) {
    this.layers.forEach((layer, i) => {
      let layer_id = layer.id || `${this.layerId}-${i}`
      if (map.getLayer(layer_id)) {
        map.removeLayer(layer_id)
      }
    })
    let { layers, fields, image_ids } = convertNbdsStyleToMapBoxStyle(style)
    SOURCE[this.url].fields = union(SOURCE[this.url].fields, fields)
    this.image_ids = image_ids
    this.layers = layers

    if (this.layers[0] && this.layers[0].filter && this.layer_label) {
      this.layer_label.filter = this.layers[0].filter
      map.setFilter(this.layer_label.id, this.layer_label.filter)
    }
    this.resetSource(map)
    this.addToMap(map)
  }
  resetSource(map) {
    map
      .getSource(this.sourceId)
      .setTiles([createSource(this.url, SOURCE[this.url].fields)])
  }
  showLabel(map, show) {
    this.label_show = show
    if (!this.layer_labels) {
      const contentLabels = this.info.style?.content_label ?? []
      const fields = []
      this.layer_labels = contentLabels.map((item, index) => {
        const defaultStyle = getDefaultLabelMapBoxStyle(this.info.type)
        const { layout, paint } = splitStyle(item.style ?? {})

        const layer = {
          ...defaultStyle,
          id: `${this.layerId}-label-${index}`,
          source: this.sourceId,
          'source-layer': 'tile-server',
          layout: {
            ...defaultStyle.layout,
            'text-field': ['get', item.field || 'name'],
            ...layout,
          },
          paint: {
            ...defaultStyle.paint,
            ...paint,
          },
        }
        if (item.field && !fields.includes(item.field)) fields.push(item.field)
        const minzoom = Number(item.minzoom)
        if (
          item.minzoom !== undefined &&
          item.minzoom !== null &&
          Number.isFinite(minzoom)
        ) {
          layer.minzoom = minzoom
        }

        const maxzoom = Number(item.maxzoom)
        if (
          item.maxzoom !== undefined &&
          item.maxzoom !== null &&
          Number.isFinite(maxzoom)
        ) {
          layer.maxzoom = maxzoom
        }

        if (item.rules?.length) {
          layer.filter = [
            'all',
            ...item.rules.map((rule) => {
              let opt = rule.opt
              if (opt === '=') opt = '=='
              return [opt, rule.first, rule.second]
            }),
          ]
        } else if (this.layers[0]?.filter) {
          layer.filter = this.layers[0].filter
        }

        return layer
      })

      SOURCE[this.url].fields = union(SOURCE[this.url].fields, fields)
      if (!contentLabels || contentLabels.length < 1) {
        const defaultStyle = getDefaultLabelMapBoxStyle(this.info.type)
        this.layer_labels = [
          {
            ...defaultStyle,
            id: `${this.layerId}-label-layer`,
            source: this.sourceId,
            'source-layer': 'tile-server',
            layout: {
              ...defaultStyle.layout,
              'text-field': ['get', 'name'],
            },
            paint: {
              ...defaultStyle.paint,
            },
          },
        ]
      }
    }

    if (this.layer_labels)
      this.layer_labels.forEach((layer) => {
        if (!map.getLayer(layer.id)) {
          map.addLayer(layer)
        }

        if (layer.filter) {
          map.setFilter(layer.id, layer.filter)
        }

        map.setLayoutProperty(layer.id, 'visibility', show ? 'visible' : 'none')
      })

    this.resetSource(map)
  }
}
export function convertNbdsStyleToMapBoxStyle(style) {
  let layers = []
  let content = style.content
  let fields = []
  let image_ids = []
  layers = content.map((x) => {
    let style = x.style
    let icon = style.icon
    let temp = {}
    switch (x.type) {
      case 'Point':
        if (icon['icon_type'] == 'shape') {
          temp = {
            type: 'circle',
            paint: {
              'circle-radius': +(style['size'] ?? 5),
              'circle-color': icon['fill'] ?? '#000',
              'circle-opacity': +(icon['fill-opacity'] ?? 1),
            },
          }
          if (icon['use_stroke']) {
            temp['paint']['circle-stroke-width'] = +(icon['stroke-width'] ?? 1)
            temp['paint']['circle-stroke-opacity'] = +(
              icon['stroke-opacity'] ?? 1
            )
            temp['paint']['circle-stroke-color'] = icon['stroke'] ?? '#000'
          }
        } else if (icon['icon_type'] == 'image') {
          temp = {
            type: 'symbol',
            layout: {
              'icon-image': '_custom_image_' + icon['image_id'],
              'icon-size': +(style['size'] ?? 1),
            },
            paint: {
              'icon-opacity': +(style['opacity'] ?? 1),
            },
          }
          image_ids.push(icon['image_id'])
        }
        break
      case 'Line':
        temp = {
          type: 'line',
          paint: {
            'line-width': +(style['stroke-width'] ?? 5),
            'line-color': style['stroke'] ?? '#000000',
            'line-opacity': +(style['stroke-opacity'] ?? 1),
          },
          layout: {},
        }
        if (style['stroke-dasharray']) {
          temp['paint']['line-dasharray'] = style['stroke-dasharray']
        }
        if (style['line-join']) {
          temp['layout']['line-join'] = style['line-join']
        }
        if (style['line-cap']) {
          temp['layout']['line-cap'] = style['line-cap']
        }
        if (style['line-pattern-image_id']) {
          temp['paint']['line-pattern'] =
            '_custom_image_' + style['line-pattern-image_id']
          image_ids.push(style['line-pattern-image_id'])
        }
        break
      case 'Polygon':
        temp = {
          type: 'fill',
          paint: {
            'fill-color': style['fill'] ?? '#000000',
            'fill-opacity': +(style['fill-opacity'] ?? 1),
          },
        }
        if (style['use_stroke']) {
          temp['paint']['fill-outline-color'] = style['stroke'] ?? '#000'
        }
        if (style['fill-pattern-image_id']) {
          temp['paint']['fill-pattern'] =
            '_custom_image_' + style['fill-pattern-image_id']
          image_ids.push(style['fill-pattern-image_id'])
        }
        break
      case 'Raster':
        temp = {
          type: 'raster',
          paint: {
            'raster-opacity': +(style['raster-opacity'] ?? 1),
          },
        }
        break
    }
    let rules = x.rules
    if (rules && rules.length > 0) {
      temp['filter'] = [
        'all',
        ...rules.map((x) => {
          let opt = x.opt
          if (opt == '=') {
            opt = '=='
          }
          if (!fields.includes(x.first)) fields.push(x.first)
          return [opt, x.first, x.second]
        }),
      ]
    }

    const minzoom = Number(x.minzoom)
    if (
      x.minzoom !== undefined &&
      x.minzoom !== null &&
      Number.isFinite(minzoom)
    ) {
      temp.minzoom = minzoom
    }

    const maxzoom = Number(x.maxzoom)
    if (
      x.maxzoom !== undefined &&
      x.maxzoom !== null &&
      Number.isFinite(maxzoom)
    ) {
      temp.maxzoom = maxzoom
    }
    return temp
  })
  return { layers, fields, image_ids }
}

export function getDefaultLabelMapBoxStyle(type) {
  let temp = undefined
  switch (type) {
    case 'Point':
    case 'MultiPoint':
      temp = {
        type: 'symbol',
        layout: {
          'text-size': 16,
          'text-offset': [0, 1],
          'text-ignore-placement': true,
        },
        paint: {
          'text-color': '#333',
          'text-halo-color': 'hsl(0, 0%, 100%)',
          'text-halo-width': 1.5,
          'text-halo-blur': 1,
        },
      }
      break
    case 'LineString':
    case 'MultiLineString':
      temp = {
        type: 'symbol',
        layout: {
          'symbol-placement': 'line',
          'text-size': 16,
        },
        paint: {
          'text-color': '#333',
          'text-halo-color': 'hsl(0, 0%, 100%)',
          'text-halo-width': 1.5,
          'text-halo-blur': 1,
        },
      }
      break

    default:
      temp = {
        type: 'symbol',
        layout: {
          'text-size': 16,
        },
        paint: {
          'text-color': '#333',
          'text-halo-color': 'hsl(0, 0%, 100%)',
          'text-halo-width': 1.5,
          'text-halo-blur': 1,
        },
      }
      break
  }
  return temp
}
const layoutProps = new Set([
  // common
  'visibility',

  // symbol
  'symbol-placement',
  'symbol-spacing',
  'symbol-avoid-edges',
  'symbol-sort-key',
  'symbol-z-order',

  // icon
  'icon-image',
  'icon-size',
  'icon-text-fit',
  'icon-text-fit-padding',
  'icon-anchor',
  'icon-pitch-alignment',
  'icon-rotation-alignment',
  'icon-allow-overlap',
  'icon-ignore-placement',
  'icon-optional',
  'icon-rotate',
  'icon-padding',
  'icon-keep-upright',
  'icon-offset',

  // text
  'text-field',
  'text-font',
  'text-size',
  'text-max-width',
  'text-line-height',
  'text-letter-spacing',
  'text-justify',
  'text-radial-offset',
  'text-variable-anchor',
  'text-anchor',
  'text-max-angle',
  'text-writing-mode',
  'text-rotate',
  'text-padding',
  'text-keep-upright',
  'text-transform',
  'text-offset',
  'text-allow-overlap',
  'text-ignore-placement',
  'text-optional',
  'text-rotation-alignment',
  'text-pitch-alignment',

  // line
  'line-cap',
  'line-join',
  'line-miter-limit',
  'line-round-limit',
])
const numberProps = new Set([
  'text-size',
  'text-max-width',
  'text-line-height',
  'text-letter-spacing',
  'text-radial-offset',
  'text-max-angle',
  'text-rotate',
  'text-padding',

  'text-opacity',
  'text-halo-width',
  'text-halo-blur',

  'icon-size',
  'icon-rotate',
  'icon-padding',

  'line-width',
  'line-gap-width',
  'line-offset',
  'line-opacity',
  'line-blur',

  'fill-opacity',

  'circle-radius',
  'circle-opacity',
  'circle-stroke-width',
  'circle-stroke-opacity',
])
function splitStyle(style = {}) {
  const layout = {}
  const paint = {}

  for (const [key, value] of Object.entries(style)) {
    let v = value

    if (numberProps.has(key) && value !== '' && value != null) {
      v = Number(value)
    }

    if (layoutProps.has(key)) {
      layout[key] = v
    } else {
      paint[key] = v
    }
  }
  if (layout['text-font']) {
    layout['text-font'] = Array.isArray(layout['text-font'])
      ? layout['text-font']
      : layout['text-font']
          .split(',')
          .map((x) => x.trim())
          .filter(Boolean)
  }
  return { layout, paint }
}
